import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import style from "../common.module.scss";
import OrdersSectionItemTableStatus from "./OrdersSectionItemTableStatus";
import { numberToFinanceFormat } from "../../../../utils";
import Loading from "../../../shared/Loading/Loading";
import moment from "moment";
import "moment/locale/ru";
import "moment/locale/kk";
import "moment/locale/zh-cn";
import EmptyContent from "../../../shared/emptyContent";
import DistanceIcon from "../../../shared/icons/DistanceIcon";
import ArrowDownIcon from "../../../shared/icons/ArrowDownIcon";
import { useTranslation } from "react-i18next";
import { CURRENCIES } from "../../../../mock";
import NotificationIconNew from "../../../shared/icons/NotificationIconNew";
import NotificationsPopup from "../../../notification/NotificationsPopup/NotificationsPopup";
import { getCountryIcon } from "../../../../utils/getCountryIcon";
import { getVehicleTypeName } from "../../../../utils/getVehicleTypeName";
import AppTooltip from "../../../shared/AppTooltip/AppTooltip";
import { UsersIcon } from "lucide-react";
import TenderAssignmentMethodIcon from "../../../shared/icons/TenderAssignmentMethodIcon";
import AuctionAssignmentMethodIcon from "../../../shared/icons/AuctionAssignmentMethodIcon";

const ASSIGNMENT_METHOD_ICONS = {
  Tender: {
    Icon: TenderAssignmentMethodIcon,
    labelKey: "ordersAvailablePage.tenderInProgress",
  },
  Auction: {
    Icon: AuctionAssignmentMethodIcon,
    labelKey: "ordersAvailablePage.auctionInProgress",
  },
};

const OrdersSectionItemTable = ({
  isLoading = false,
  items = [],
  isExpeditor = false,
  isRailway = false,
  notificationsMap = {},
  setNotificationsMap,
  selectedIds = [],
  setSelectedIds,
  unselectedIds = [],
  setUnselectedIds = () => {},
  selectionMode = "partial",
  onOpenOrder,
}) => {
  const { t, i18n  } = useTranslation();
  const notificationRefs = useRef([]);

  const [openNotificationOrderId, setOpenNotificationOrderId] = useState(null);

  const hasShippingHourFrom = (item) => Number(item?.shippingHourFrom) > 0;

  const handleToggleNotification = (orderId) => {
    setOpenNotificationOrderId((prevId) => (prevId === orderId ? null : orderId));
  };

  const currentPageIds = useMemo(() => items.map((item) => item.id).filter(Boolean), [items]);
  const isOrderSelected = useCallback(
    (itemId) => {
      if (selectionMode === "all") {
        return !unselectedIds.includes(itemId);
      }

      return selectedIds.includes(itemId);
    },
    [selectedIds, selectionMode, unselectedIds],
  );
  const isCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every(isOrderSelected);

  const handleToggleCheckbox = (itemId) => {
    if (selectionMode === "all") {
      setUnselectedIds((prev) => {
        if (prev.includes(itemId)) {
          return prev.filter((id) => id !== itemId);
        }

        return [...prev, itemId];
      });
      return;
    }

    setSelectedIds((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      }

      return [...prev, itemId];
    });
  };

  const handleToggleSelectAll = () => {
    if (selectionMode === "all") {
      setUnselectedIds((prev) => {
        if (isCurrentPageSelected) {
          return Array.from(new Set([...prev, ...currentPageIds]));
        }

        return prev.filter((id) => !currentPageIds.includes(id));
      });
      return;
    }

    setSelectedIds((prev) => {
      if (isCurrentPageSelected) {
        return prev.filter((id) => !currentPageIds.includes(id));
      }

      return Array.from(new Set([...prev, ...currentPageIds]));
    });
  };

  const handleCheckboxChange = (event) => {
    event.stopPropagation();
  };

  const handleOpenOrderInfo = (orderId) => {
    onOpenOrder?.(orderId);
  };

  useEffect(() => {
    const langAliasMap = {
      ru: "ru",
      kz: "kk",
      cn: "zh-cn",
      en: "en",
    };

    const onLanguageChanged = (lng) => {
      const momentLocale = langAliasMap[lng] || "en";
      moment.locale(momentLocale);
    };

    // Подписка на событие смены языка
    i18n.on('languageChanged', onLanguageChanged);

    // Установка при первом рендере
    onLanguageChanged(i18n.language);

    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, []);



  const tableHead = useMemo(() => {
    return [
      {
        name: "",
        isSortable: false,
        key: "checkbox",
      },
      {
        name: t("ordersAvailablePage.tableHead.details"),
        isSortable: false,
        key: "number",
      },
      {
        name: t("ordersAvailablePage.tableHead.from"),
        isSortable: false,
        key: "addressFrom",
      },
      {
        component: <DistanceIcon />,
        isSortable: false,
        key: "addressTo",
      },
      {
        name: t("ordersAvailablePage.tableHead.to"),
        isSortable: false,
        key: "addressTo",
      },
      {
        name: t("ordersAvailablePage.tableHead.status"),
        isSortable: false,
      },
      {
        name: t(isRailway ? "railwayOrder.wagonType" : "ordersAvailablePage.tableHead.transport"),
        isSortable: false,
      },
      {
        name: "",
        isSortable: false,
      },
    ];
  }, [isExpeditor, isRailway, t]);

  return (
    <>
      {isLoading ? (
        <Loading inBlock />
      ) : !items.length ? (
        <EmptyContent />
      ) : (
        <div className={cn(style.table_wrapper, "table-responsive")}>
          <table className="table">
            <thead>
              <tr>
                {tableHead.map((item, idx) => (
                  <th scope="col" key={"head-" + idx}>
                    {idx === 0 ? (
                      <div className={style.checkboxWrapper} onClick={handleToggleSelectAll}>
                        <input
                          type="checkbox"
                          className={style.checkboxTable}
                          checked={isCurrentPageSelected}
                          onChange={handleCheckboxChange}
                          readOnly
                        />
                        <div className={style.checkboxCustom}>
                          {isCurrentPageSelected && (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className={style.tableHead}>{item.component ? item.component : item.name}</div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                if (!notificationRefs.current[idx]) {
                  notificationRefs.current[idx] = React.createRef();
                }
                const unreadCount = (notificationsMap[item.id] || []).filter((n) => !n.isRead).length;
                const assignmentMethodIcon =
                  item.status?.toLowerCase() === "awaitingoffer" && item.isComplexTender === false
                    ? ASSIGNMENT_METHOD_ICONS[item.assignmentMethod]
                    : null;
                const AssignmentMethodIcon = assignmentMethodIcon?.Icon;

                return (
                  <tr key={item.number + idx} className="cursor-pointer">
                    <td>
                      <div
                        className={style.checkboxWrapper}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleCheckbox(item.id);
                        }}
                      >
                        <input
                          type="checkbox"
                          className={style.checkboxTable}
                          checked={isOrderSelected(item.id)}
                          onChange={handleCheckboxChange}
                          readOnly
                        />
                        <div className={style.checkboxCustom}>
                          {isOrderSelected(item.id) && (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                    </td>
                    <td onClick={() => handleOpenOrderInfo(item.id)}>
                      <div className={style.table_col} style={item?.referenceNumber ? { gap: 0 } : undefined}>
                        {item?.isComplexTender ? (
                          <div className={style.table_col_vehicles}>
                            {(item?.vehiclesCount ?? 0) - (item?.acceptedVehiclesCount ?? 0)}
                            <UsersIcon size={20} color="#004de6" />
                          </div>
                        ) : AssignmentMethodIcon ? (
                          <div className={style.table_col_vehicles}>
                            <AssignmentMethodIcon
                              color="#004de6"
                              width="24"
                              height="24"
                              role="img"
                              aria-label={t(assignmentMethodIcon.labelKey)}
                            />
                          </div>
                        ) : (
                          <span>
                            {numberToFinanceFormat(item.price)}{" "}
                            {CURRENCIES[item?.currency || "KZT"]?.symbol || ""}
                          </span>
                        )}
                        <span className={style.table_col_summary}>№ {item.number}</span>
                        {item?.referenceNumber && (
                          <>
                            <span
                              className={cn(style.table_col_summary, style.table_col_reference)}
                              data-tooltip-id={`referenceNumber-tooltip-${item.id}`}
                            >
                              №{item?.referenceNumber}
                            </span>
                            <AppTooltip
                              id={`referenceNumber-tooltip-${item.id}`}
                              delayShow={50}
                              positionStrategy="fixed"
                            >
                              №{item?.referenceNumber}
                            </AppTooltip>
                          </>
                        )}
                      </div>
                    </td>
                    <td onClick={() => handleOpenOrderInfo(item.id)}>
                      <div className={style.table_col}>
                        <div className={style.table_col_country}>
                          <img
                            alt="countryIcon"
                            src={getCountryIcon(item.countryIdFrom)}
                            className={style.table_col_countryIcon}
                          />
                          {item.cityFromName}
                        </div>
                        <span className={style.table_col_summary}>
                          {item?.shippingDate
                            ? hasShippingHourFrom(item)
                              ? moment
                                  .utc(item.shippingDate)
                                  .startOf("day")
                                  .add(item.shippingHourFrom, "hours")
                                  .format("D MMMM, HH:mm")
                              : moment.utc(item.shippingDate).format("D MMMM")
                            : t("orderInfo.ordersInfoDirections.awaitingDateAssignment")}
                        </span>
                      </div>
                    </td>
                    <td onClick={() => handleOpenOrderInfo(item.id)}>
                      {/* <div className={style.table_col_distance}>
                        {numberToFinanceFormat(item.distance || "-")}
                        {t("analytics.distance_metric")}
                      </div> */}
                    </td>
                    <td onClick={() => handleOpenOrderInfo(item.id)}>
                      <div className={style.table_col}>
                        <div className={style.table_col_country}>
                          <img
                            alt="countryIcon"
                            src={getCountryIcon(item.countryIdTo)}
                            className={style.table_col_countryIcon}
                          />
                          {item.cityToName}
                        </div>
                        <span className={style.table_col_summary}>
                          {item?.unloadingDate
                            ? moment
                                .utc(item.unloadingDate)
                                .startOf("day")
                                .add(item.unloadingHourFrom, "hours")
                                .format("D MMMM, HH:mm")
                            : t("orderInfo.ordersInfoDirections.awaitingDateAssignment")}
                        </span>
                      </div>
                    </td>
                    <td onClick={() => handleOpenOrderInfo(item.id)}>
                      <div>
                        <OrdersSectionItemTableStatus
                          timeSnapshotKey={item}
                          status={item.status}
                          driverName={item.driverName}
                          driverSurname={item.driverSurname}
                          expeditorName={item?.expeditorName}
                          tenderDateEnd={item?.tenderDateEnd}
                          assignmentMethod={item?.assignmentMethod}
                        />
                      </div>
                    </td>
                    <td onClick={() => handleOpenOrderInfo(item.id)}>
                      <div className={style.table_col}>
                        <div className={style.table_col_country}>
                          {isRailway ? item.wagonTypeName || "—" : getVehicleTypeName(item, t)}
                        </div>
                        {!isRailway && <span className={style.table_col_summary}>{item.carModel}</span>}
                      </div>
                    </td>
                    <td>
                      <div className={style.table_col_arrow}>
                        <div
                          className={cn("ms-auto", style.notification)}
                          ref={notificationRefs.current[idx]}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleNotification(item.id);
                          }}
                        >
                          <NotificationIconNew />
                          {unreadCount > 0 && <div className={style.notification_count}>{unreadCount}</div>}
                        </div>

                        <NotificationsPopup
                          notifications={notificationsMap[item.id] || []}
                          orderData={item}
                          orderType="order"
                          isOpen={openNotificationOrderId === item.id}
                          onClose={() => {
                            setOpenNotificationOrderId(null);
                          }}
                          referenceElement={notificationRefs.current[idx]?.current}
                          onNotificationsRead={(readIds) => {
                            setNotificationsMap((prev) => ({
                              ...prev,
                              [item.id]: prev[item.id].map((notif) =>
                                readIds.includes(notif.id)
                                  ? { ...notif, isRead: true, readDate: new Date() }
                                  : notif,
                              ),
                            }));
                          }}
                        />

                        <ArrowDownIcon
                          style={{ transform: "rotate(270deg)" }}
                          onClick={() => handleOpenOrderInfo(item.id)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>


        </div>
      )}
    </>
  );
};

export default OrdersSectionItemTable;
